import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Users,
  HardDrive,
  Activity,
  DollarSign
} from 'lucide-react';
import { TenantFilters, TenantFilterOptions } from './TenantFilters';
import { TenantActions, BulkActions, QuickActions } from './TenantActions';
import { PlanChangeModal } from './PlanChangeModal';
import { SuspensionModal } from './SuspensionModal';
import { TenantManageModal } from './TenantManageModal';
import { api } from '../auth/authStore';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  customDomain?: string;
  plan: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  status: 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'INACTIVE';
  billing: {
    status: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'CANCELLED';
    trialEnd?: string;
    totalRevenue: number;
  };
  usage: {
    users: { current: number; limit: number };
    storage: { current: number; limit: number };
    apiCalls: { current: number; limit: number };
  };
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
  };
  lastActivityAt: string;
  createdAt: string;
}



export const TenantManagement: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TenantFilterOptions>({});
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal states
  const [planChangeModal, setPlanChangeModal] = useState<{
    isOpen: boolean;
    tenantId: string;
    tenantName: string;
    currentPlan: string;
  }>({
    isOpen: false,
    tenantId: '',
    tenantName: '',
    currentPlan: ''
  });

  const [suspensionModal, setSuspensionModal] = useState<{
    isOpen: boolean;
    tenantId: string;
    tenantName: string;
    currentStatus: string;
  }>({
    isOpen: false,
    tenantId: '',
    tenantName: '',
    currentStatus: ''
  });

  const [manageModal, setManageModal] = useState<{
    isOpen: boolean;
    tenantId: string;
    tenantName: string;
  }>({
    isOpen: false,
    tenantId: '',
    tenantName: ''
  });

  useEffect(() => {
    fetchTenants();
  }, [filters, currentPage]);

  const fetchTenants = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', '20');

      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else if (typeof value === 'object') {
            Object.entries(value).forEach(([subKey, subValue]) => {
              if (subValue !== undefined && subValue !== null) {
                params.append(`${key}.${subKey}`, subValue.toString());
              }
            });
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await api.get(`/platform/tenants?${params}`);

      if (response.data.success) {
        setTenants(response.data.data);
        setTotalPages(response.data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'TRIAL':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'SUSPENDED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'TRIAL':
        return 'bg-yellow-100 text-yellow-800';
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'STARTER':
        return 'bg-blue-100 text-blue-800';
      case 'PROFESSIONAL':
        return 'bg-purple-100 text-purple-800';
      case 'ENTERPRISE':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatUsagePercentage = (current: number, limit: number) => {
    const percentage = (current / limit) * 100;
    return Math.round(percentage);
  };

  const getUsageColor = (current: number, limit: number) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Action handlers
  const handleViewTenant = (tenantId: string) => {
    navigate(`/platform/tenants/${tenantId}`);
  };

  const handleEditTenant = (tenantId: string) => {
    navigate(`/platform/tenants/${tenantId}/edit`);
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (!confirm('Are you sure you want to delete this firm? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/platform/tenants/${tenantId}`);

      if (response.data.success) {
        alert('Firm deleted successfully!');
        fetchTenants(); // Refresh the firm list
      } else {
        console.error('Failed to delete firm:', response.data.message);
        alert('Failed to delete firm: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Failed to delete firm:', error);
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to delete firm';
      alert('Error: ' + errorMessage);
    }
  };

  const handleSuspendTenant = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setSuspensionModal({
        isOpen: true,
        tenantId,
        tenantName: tenant.name,
        currentStatus: tenant.status
      });
    }
  };

  const handleReactivateTenant = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setSuspensionModal({
        isOpen: true,
        tenantId,
        tenantName: tenant.name,
        currentStatus: tenant.status
      });
    }
  };

  const handleChangePlan = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setPlanChangeModal({
        isOpen: true,
        tenantId,
        tenantName: tenant.name,
        currentPlan: tenant.plan
      });
    }
  };

  const handleManageTenant = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setManageModal({
        isOpen: true,
        tenantId,
        tenantName: tenant.name
      });
    }
  };

  const handlePlanChange = async (tenantId: string, newPlan: string, reason: string) => {
    try {
      const response = await api.post(`/platform/tenants/${tenantId}/change-plan`, {
        plan: newPlan,
        reason
      });

      if (response.data.success) {
        fetchTenants();
        setPlanChangeModal({ isOpen: false, tenantId: '', tenantName: '', currentPlan: '' });
      } else {
        console.error('Failed to change plan:', response.data.message);
        throw new Error(response.data.message || 'Failed to change plan');
      }
    } catch (error) {
      console.error('Failed to change plan:', error);
      throw error;
    }
  };

  const handleSuspension = async (tenantId: string, reason: string) => {
    try {
      const response = await api.post(`/platform/tenants/${tenantId}/suspend`, { reason });

      if (response.data.success) {
        fetchTenants();
        setSuspensionModal({ isOpen: false, tenantId: '', tenantName: '', currentStatus: '' });
      }
    } catch (error) {
      console.error('Failed to suspend firm:', error);
      throw error;
    }
  };

  const handleReactivation = async (tenantId: string) => {
    try {
      const response = await api.post(`/platform/tenants/${tenantId}/reactivate`);

      if (response.data.success) {
        fetchTenants();
        setSuspensionModal({ isOpen: false, tenantId: '', tenantName: '', currentStatus: '' });
      }
    } catch (error) {
      console.error('Failed to reactivate firm:', error);
      throw error;
    }
  };

  const handleBulkAction = async (action: string, tenantIds: string[]) => {
    try {
      switch (action) {
        case 'export':
          console.log('Exporting firms:', tenantIds);
          break;
        case 'suspend':
          // Handle bulk suspension
          break;
        case 'reactivate':
          // Handle bulk reactivation
          break;
        case 'delete':
          // Handle bulk deletion
          break;
        default:
          console.log('Bulk action:', action, tenantIds);
      }
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
    }
  };

  const toggleTenantSelection = (tenantId: string) => {
    setSelectedTenants(prev => 
      prev.includes(tenantId) 
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const selectAllTenants = () => {
    if (selectedTenants.length === tenants.length) {
      setSelectedTenants([]);
    } else {
      setSelectedTenants(tenants.map(t => t.id));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading firms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Firm Management</h1>
              <p className="text-gray-600 mt-1">
                Manage and monitor all platform firms
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/platform/tenants/new')}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Firm
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <TenantFilters
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={() => setFilters({})}
        isOpen={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
      />

      {/* Search and Actions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Bulk Actions */}
        <BulkActions
          selectedTenants={selectedTenants}
          onBulkAction={handleBulkAction}
          onClearSelection={() => setSelectedTenants([])}
        />

        {/* Tenants Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input 
                      type="checkbox" 
                      checked={selectedTenants.length === tenants.length && tenants.length > 0}
                      onChange={selectAllTenants}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Firm
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input 
                        type="checkbox" 
                        checked={selectedTenants.includes(tenant.id)}
                        onChange={() => toggleTenantSelection(tenant.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                          <Building2 className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                          <div className="text-sm text-gray-500">{tenant.subdomain}.erpca.ca</div>
                          <div className="text-xs text-gray-400">{tenant.contact.company}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getStatusIcon(tenant.status)}
                        <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tenant.status)}`}>
                          {tenant.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(tenant.plan)}`}>
                        {tenant.plan}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          <span className={getUsageColor(tenant.usage.users.current, tenant.usage.users.limit)}>
                            {formatUsagePercentage(tenant.usage.users.current, tenant.usage.users.limit)}%
                          </span>
                        </div>
                        <div className="flex items-center text-xs">
                          <HardDrive className="w-3 h-3 mr-1" />
                          <span className={getUsageColor(tenant.usage.storage.current, tenant.usage.storage.limit)}>
                            {formatUsagePercentage(tenant.usage.storage.current, tenant.usage.storage.limit)}%
                          </span>
                        </div>
                        <div className="flex items-center text-xs">
                          <Activity className="w-3 h-3 mr-1" />
                          <span className={getUsageColor(tenant.usage.apiCalls.current, tenant.usage.apiCalls.limit)}>
                            {formatUsagePercentage(tenant.usage.apiCalls.current, tenant.usage.apiCalls.limit)}%
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm">
                        <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                        <span className="font-medium">${tenant.billing.totalRevenue.toLocaleString()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(tenant.lastActivityAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <QuickActions
                          tenantId={tenant.id}
                          status={tenant.status}
                          onView={handleViewTenant}
                          onEdit={handleEditTenant}
                          onSuspend={handleSuspendTenant}
                          onReactivate={handleReactivateTenant}
                        />
                        <TenantActions
                          tenantId={tenant.id}
                          tenantName={tenant.name}
                          status={tenant.status}
                          onEdit={handleEditTenant}
                          onView={handleViewTenant}
                          onDelete={handleDeleteTenant}
                          onSuspend={handleSuspendTenant}
                          onReactivate={handleReactivateTenant}
                          onChangePlan={handleChangePlan}
                          onViewAnalytics={(id) => navigate(`/platform/tenants/${id}/analytics`)}
                          onManage={handleManageTenant}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <PlanChangeModal
        isOpen={planChangeModal.isOpen}
        onClose={() => setPlanChangeModal({ isOpen: false, tenantId: '', tenantName: '', currentPlan: '' })}
        tenantId={planChangeModal.tenantId}
        tenantName={planChangeModal.tenantName}
        currentPlan={planChangeModal.currentPlan}
        onPlanChange={handlePlanChange}
      />

      <SuspensionModal
        isOpen={suspensionModal.isOpen}
        onClose={() => setSuspensionModal({ isOpen: false, tenantId: '', tenantName: '', currentStatus: '' })}
        tenantId={suspensionModal.tenantId}
        tenantName={suspensionModal.tenantName}
        currentStatus={suspensionModal.currentStatus}
        onSuspend={handleSuspension}
        onReactivate={handleReactivation}
      />

      <TenantManageModal
        isOpen={manageModal.isOpen}
        onClose={() => setManageModal({ isOpen: false, tenantId: '', tenantName: '' })}
        tenantId={manageModal.tenantId}
        tenantName={manageModal.tenantName}
        onTenantUpdate={fetchTenants}
      />
    </div>
  );
};
